import React, {Component, useContext, useEffect, useRef, useState} from 'react'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeTemplate from '../../controllers/LimeTemplate'
import TemplateData from '../../controllers/TemplateData'
import Input from '../../components/Input'
import Number from '../../components/Number'
import Switch from '../../components/Switch'
import {SelectEnum} from '../../components/SelectEnum'
import Stepper, {StepTab, StepperRef} from '../../components/Stepper'
import BalancingNormBasis from '../../controllers/BalancingNormBasis'
import GypsumPurpose from '../../controllers/GypsumPurpose'
import MainCaSource from '../../controllers/MainCaSource'
import KRecommendationBasis from '../../controllers/KRecommendationBasis'
import {buildSetter} from '../../immutableState'
import {
    ELEMENT_SPECIFIC_GRAVITY,
    ELEMENTAL_SULPHUR_BY_TEXTURE,
    BASE_SATURATION_BY_DENSITY_STATIC,
    BASE_SATURATION_BY_CEC_STATIC
} from './staticdata'
import {useNavigate, useParams, useBlocker, useSearchParams} from "react-router-dom";
import {showSuccessOrFailed} from '../../Snacks';
import {EditRow} from "../../components/Fields";
import Dialog from '../../components/Dialog';
import ProgramApprove from '../program/ProgramApprove';
import Success from '../../components/Success';

// Helper components
const FieldRow: React.FC<{label: string, children: React.ReactNode}> = ({label, children}) => (
    <div className="flex items-center py-1">
        <span className="text-xs text-gray-700 w-44 flex-shrink-0">{label}</span>
        <div className="flex items-center gap-1">
            {children}
        </div>
    </div>
)

const NumberField: React.FC<{label: string, value: number, onChange: (v: number) => void, unit?: string, fractionDigits?: number}> = ({label, value, onChange, unit, fractionDigits}) => (
    <FieldRow label={label}>
        <Number value={value} change={onChange} fractionDigits={fractionDigits} />
        {unit && <span className="text-sm text-gray-500">{unit}</span>}
    </FieldRow>
)

const BooleanField: React.FC<{label: string, value: boolean, onChange: (v: boolean) => void}> = ({label, value, onChange}) => (
    <FieldRow label={label}>
        <Switch checked={value} setChecked={onChange} />
    </FieldRow>
)

const SectionBlock: React.FC<{title: string, children: React.ReactNode}> = ({title, children}) => (
    <div className="bg-white shadow rounded-lg p-4 h-fit">
        <h2 className="text-lg font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">{title}</h2>
        {children}
    </div>
)

const SubSection: React.FC<{title: string, children: React.ReactNode}> = ({title, children}) => (
    <div className="mt-3 first:mt-0">
        <h3 className="text-sm font-medium text-gray-700 mb-2">{title}</h3>
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            {children}
        </div>
    </div>
)

// Static data table (orange background - do not edit)
const StaticTable: React.FC<{title: string, children: React.ReactNode}> = ({title, children}) => (
    <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
            <h4 className="text-md font-medium text-orange-800">{title}</h4>
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Do Not Edit</span>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
            {children}
        </div>
    </div>
)

// Store type for updating template data using buildSetter pattern
type TemplateStore = {
    data: TemplateData
    set: (partial: Partial<TemplateData>) => void
}

// Section components
const ElementalSulphurSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.elementalSulphur

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({elementalSulphur: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Elemental Sulphur application (kg/ha)">
            <table className="w-full text-xs">
                {/* Editable parameters header */}
                <thead>
                <tr className="bg-blue-50 border-b border-blue-200">
                    <th className="px-2 py-1 text-left" colSpan={2}>
                        <div className="flex items-center gap-2">
                            <span>Enabled</span>
                            <Switch checked={params.enabled} setChecked={v => update('enabled', v)} />
                        </div>
                    </th>
                    <th className="px-2 py-1 text-right">
                        <div className="flex items-center justify-end gap-1">
                            <span>Depth (cm)</span>
                            <Number value={params.depthCm} change={v => update('depthCm', v)} />
                        </div>
                    </th>
                    <th className="px-2 py-1 text-right" colSpan={3}>
                        <div className="flex items-center justify-end gap-1">
                            <span>Target pH</span>
                            <Number value={params.targetPh} change={v => update('targetPh', v)} fractionDigits={1} />
                        </div>
                    </th>
                </tr>
                {/* Static data headers */}
                <tr className="bg-orange-100">
                    <th className="px-2 py-1 text-left">Texture</th>
                    <th className="px-2 py-1 text-right">CEC</th>
                    <th className="px-2 py-1 text-right">To pH 8.5</th>
                    <th className="px-2 py-1 text-right">To pH 8.0</th>
                    <th className="px-2 py-1 text-right">To pH 7.5</th>
                    <th className="px-2 py-1 text-right">To pH 7.0</th>
                </tr>
                </thead>
                <tbody className="bg-orange-50">
                {ELEMENTAL_SULPHUR_BY_TEXTURE.map((row, i) => (
                    <tr key={i} className="border-t border-orange-200">
                        <td className="px-2 py-1">{row.texture}</td>
                        <td className="px-2 py-1 text-right">{row.operatingCec}</td>
                        <td className="px-2 py-1 text-right">{row.toPh85.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right">{row.toPh80.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right">{row.toPh75.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right">{row.toPh70.toFixed(2)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </SectionBlock>
    )
}

const SurfaceApplicationSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.surfaceApplication

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({surfaceApplication: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Trees/Ridges adjusters">
            <table className="w-full text-xs">
                <tbody>
                <tr className="bg-blue-50 border-b border-blue-200">
                    <td className="px-2 py-1 font-medium">Activated</td>
                    <td className="px-2 py-1 text-right">
                        <Switch checked={params.activated} setChecked={v => update('activated', v)} />
                    </td>
                </tr>
                <tr className="bg-blue-50 border-b border-blue-200">
                    <td className="px-2 py-1 font-medium">Lime & Gypsum %</td>
                    <td className="px-2 py-1 text-right">
                        <Number value={params.limeGypsumPercentage * 100} change={v => update('limeGypsumPercentage', v / 100)} fractionDigits={0} />
                    </td>
                </tr>
                <tr className="bg-blue-50 border-b border-blue-200">
                    <td className="px-2 py-1 font-medium">P & K %</td>
                    <td className="px-2 py-1 text-right">
                        <Number value={params.pkPercentage * 100} change={v => update('pkPercentage', v / 100)} fractionDigits={0} />
                    </td>
                </tr>
                <tr className="bg-blue-50 border-b border-blue-200">
                    <td className="px-2 py-1 font-medium">Adjust for Coarse Fragment</td>
                    <td className="px-2 py-1 text-right">
                        <Switch checked={params.adjustCoarseFragment} setChecked={v => update('adjustCoarseFragment', v)} />
                    </td>
                </tr>
                <tr className="bg-blue-50">
                    <td className="px-2 py-1 font-medium">Reduce Lime %</td>
                    <td className="px-2 py-1 text-right">
                        <Number value={params.reduceLimePercentage * 100} change={v => update('reduceLimePercentage', v / 100)} fractionDigits={0} />
                    </td>
                </tr>
                </tbody>
            </table>
        </SectionBlock>
    )
}

const LimeApplicationSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.lime

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({lime: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Lime Application">
            <SubSection title="Basic Parameters">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <BooleanField label="Standard by pH" value={params.standardByPh} onChange={v => update('standardByPh', v)} />
                    <NumberField label="Desired pH" value={params.desiredPh} onChange={v => update('desiredPh', v)} fractionDigits={1} />
                    <NumberField label="Depth" value={params.depthCm} onChange={v => update('depthCm', v)} unit="cm" />
                    <BooleanField label="Neutralise Acid" value={params.neutraliseAcid} onChange={v => update('neutraliseAcid', v)} />
                    <BooleanField label="Apply Brakes" value={params.applyBrakes} onChange={v => update('applyBrakes', v)} />
                    <NumberField label="pH Warning Threshold" value={params.phWarningThreshold} onChange={v => update('phWarningThreshold', v)} fractionDigits={1} />
                </div>
            </SubSection>

            <SubSection title="Minimum Rate Rules">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <BooleanField label="Min Break Activated" value={params.minBreakActivated} onChange={v => update('minBreakActivated', v)} />
                    <NumberField label="Min Rate" value={params.minRateKgHa} onChange={v => update('minRateKgHa', v)} unit="kg/ha" />
                    <BooleanField label="Min Economically Feasible Break Activated" value={params.minEconomicallyFeasibleBreakActivated} onChange={v => update('minEconomicallyFeasibleBreakActivated', v)} />
                    <NumberField label="Min Economically Feasible" value={params.minEconomicallyFeasibleKgHa} onChange={v => update('minEconomicallyFeasibleKgHa', v)} unit="kg/ha" />
                </div>
            </SubSection>

            <SubSection title="Ideal Targets">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <BooleanField label="Reach Ideal Mg" value={params.reachIdealMg} onChange={v => update('reachIdealMg', v)} />
                    <NumberField label="Ideal Mg" value={params.idealMgMgKg} onChange={v => update('idealMgMgKg', v)} unit="mg/kg" />
                    <BooleanField label="Reach Ideal Ca" value={params.reachIdealCa} onChange={v => update('reachIdealCa', v)} />
                    <NumberField label="Ideal Ca" value={params.idealCaMgKg} onChange={v => update('idealCaMgKg', v)} unit="mg/kg" />
                </div>
            </SubSection>

            <SubSection title="Maximum Lime Rules by CEC">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <BooleanField label="Max Break Activated" value={params.maxBreakActivated} onChange={v => update('maxBreakActivated', v)} />
                    <BooleanField label="Break Activated" value={params.breakActivated} onChange={v => update('breakActivated', v)} />
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="px-3 py-2 text-left">CEC</th>
                        <th className="px-3 py-2 text-right">Max Lime (kg/ha)</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.entries(params.maxLimeRules).map(([key, rule]: [string, any]) => (
                        <tr key={key} className="border-t border-gray-200">
                            <td className="px-3 py-2">{rule.cec}</td>
                            <td className="px-3 py-2 text-right">
                                <Number value={rule.maxKg} change={v => {
                                    const updated = {...params.maxLimeRules, [key]: {...rule, maxKg: v}}
                                    update('maxLimeRules', updated)
                                }} />
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </SubSection>

            <SubSection title="Dolomitic Rules (Mg Threshold by CEC)">
                <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                        <tr>
                            <th className="px-3 py-2 text-left">CEC</th>
                            <th className="px-3 py-2 text-right">Mg Threshold (ppm)</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.entries(params.dolomiticRules).map(([key, rule]: [string, any]) => (
                            <tr key={key} className="border-t border-gray-200">
                                <td className="px-3 py-2">{rule.cec}</td>
                                <td className="px-3 py-2 text-right">
                                    <Number value={rule.mgThresholdPpm} change={v => {
                                        const updated = {...params.dolomiticRules, [key]: {...rule, mgThresholdPpm: v}}
                                        update('dolomiticRules', updated)
                                    }} />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </SubSection>
        </SectionBlock>
    )
}

const PhosphorusSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.phosphorus

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({phosphorus: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Phosphorus (P) Application">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <BooleanField label="Monitoring Enabled" value={params.monitoringEnabled} onChange={v => update('monitoringEnabled', v)} />
                <NumberField label="Max Application" value={params.maxApplicationKgHa} onChange={v => update('maxApplicationKgHa', v)} unit="kg/ha" />
                <NumberField label="Min Application" value={params.minApplicationKgHa} onChange={v => update('minApplicationKgHa', v)} unit="kg/ha" />
                <NumberField label="Depth" value={params.depthCm} onChange={v => update('depthCm', v)} unit="cm" />
                <BooleanField label="Split Over 2 Years" value={params.splitOver2Years} onChange={v => update('splitOver2Years', v)} />
                <BooleanField label="Use Estimated Fixing Fraction" value={params.useEstimatedFixingFraction} onChange={v => update('useEstimatedFixingFraction', v)} />
                <NumberField label="Estimated Fixing Fraction" value={params.estimatedFixingFractionValue} onChange={v => update('estimatedFixingFractionValue', v)} fractionDigits={2} />
                <NumberField label="Ideal P" value={params.idealMgKg} onChange={v => update('idealMgKg', v)} unit="mg/kg" />
            </div>
        </SectionBlock>
    )
}

const BaseSaturationSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.baseSaturation

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({baseSaturation: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Base Saturation Norms">
            <div className="mb-4">
                <FieldRow label="Balancing Norm Basis">
                    <SelectEnum
                        value={params.normBasis}
                        onChange={v => update('normBasis', v as BalancingNormBasis)}
                        options={{
                            'CEC': BalancingNormBasis.CEC,
                            'Density': BalancingNormBasis.Density
                        }}
                    />
                </FieldRow>
            </div>

            <SubSection title="By Soil CEC">
                <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                        <tr>
                            <th className="px-2 py-2 text-left">CEC</th>
                            <th className="px-2 py-2 text-right">Target Ca %</th>
                            <th className="px-2 py-2 text-right">Target Mg %</th>
                            <th className="px-2 py-2 text-right">Target K %</th>
                            <th className="px-2 py-2 text-right">Ca:Mg Ratio</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.entries(params.baseSaturationByCec).map(([key, rule]: [string, any]) => (
                            <tr key={key} className="border-t border-gray-200">
                                <td className="px-2 py-2">{rule.operatingCec}</td>
                                <td className="px-2 py-2 text-right">
                                    <Number value={rule.targetCa * 100} change={v => {
                                        const updated = {...params.baseSaturationByCec, [key]: {...rule, targetCa: v / 100}}
                                        update('baseSaturationByCec', updated)
                                    }} fractionDigits={2} />
                                </td>
                                <td className="px-2 py-2 text-right">
                                    <Number value={rule.targetMg * 100} change={v => {
                                        const updated = {...params.baseSaturationByCec, [key]: {...rule, targetMg: v / 100}}
                                        update('baseSaturationByCec', updated)
                                    }} fractionDigits={2} />
                                </td>
                                <td className="px-2 py-2 text-right">
                                    <Number value={rule.targetK * 100} change={v => {
                                        const updated = {...params.baseSaturationByCec, [key]: {...rule, targetK: v / 100}}
                                        update('baseSaturationByCec', updated)
                                    }} fractionDigits={2} />
                                </td>
                                <td className="px-2 py-2 text-right">
                                    <Number value={rule.caMgRatio} change={v => {
                                        const updated = {...params.baseSaturationByCec, [key]: {...rule, caMgRatio: v}}
                                        update('baseSaturationByCec', updated)
                                    }} fractionDigits={4} />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </SubSection>

            <SubSection title="By Soil Density">
                <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                        <tr>
                            <th className="px-2 py-2 text-left">Density</th>
                            <th className="px-2 py-2 text-right">Target Ca %</th>
                            <th className="px-2 py-2 text-right">Target Mg %</th>
                            <th className="px-2 py-2 text-right">Target K %</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.entries(params.baseSaturationByDensity).map(([key, rule]: [string, any]) => (
                            <tr key={key} className="border-t border-gray-200">
                                <td className="px-2 py-2">{rule.operatingDensity}</td>
                                <td className="px-2 py-2 text-right">
                                    <Number value={rule.targetCa * 100} change={v => {
                                        const updated = {...params.baseSaturationByDensity, [key]: {...rule, targetCa: v / 100}}
                                        update('baseSaturationByDensity', updated)
                                    }} fractionDigits={2} />
                                </td>
                                <td className="px-2 py-2 text-right">
                                    <Number value={rule.targetMg * 100} change={v => {
                                        const updated = {...params.baseSaturationByDensity, [key]: {...rule, targetMg: v / 100}}
                                        update('baseSaturationByDensity', updated)
                                    }} fractionDigits={2} />
                                </td>
                                <td className="px-2 py-2 text-right">
                                    <Number value={rule.targetK * 100} change={v => {
                                        const updated = {...params.baseSaturationByDensity, [key]: {...rule, targetK: v / 100}}
                                        update('baseSaturationByDensity', updated)
                                    }} fractionDigits={2} />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </SubSection>

            <StaticTable title="Depth Multipliers by Density">
                <table className="w-full text-sm">
                    <thead className="bg-orange-100">
                    <tr>
                        <th className="px-2 py-2 text-left">Density</th>
                        <th className="px-2 py-2 text-right">15cm</th>
                        <th className="px-2 py-2 text-right">20cm</th>
                        <th className="px-2 py-2 text-right">30cm</th>
                        <th className="px-2 py-2 text-right">40cm</th>
                        <th className="px-2 py-2 text-right">60cm</th>
                        <th className="px-2 py-2 text-right">80cm</th>
                        <th className="px-2 py-2 text-right">100cm</th>
                        <th className="px-2 py-2 text-right">120cm</th>
                    </tr>
                    </thead>
                    <tbody>
                    {BASE_SATURATION_BY_DENSITY_STATIC.map((row, i) => (
                        <tr key={i} className="border-t border-orange-200">
                            <td className="px-2 py-2">{row.operatingDensity}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth15}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth20}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth30}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth40}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth60}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth80}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth100}</td>
                            <td className="px-2 py-2 text-right">{row.depthMultipliers.depth120}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </StaticTable>

            <StaticTable title="% Base Saturation Norms by Soil CEC">
                <table className="w-full text-sm">
                    <thead className="bg-orange-100">
                    <tr>
                        <th className="px-2 py-2 text-left">CEC Range</th>
                        <th className="px-2 py-2 text-right">Op. CEC</th>
                        <th className="px-2 py-2 text-right">Ca %</th>
                        <th className="px-2 py-2 text-right">Mg %</th>
                        <th className="px-2 py-2 text-right">K %</th>
                        <th className="px-2 py-2 text-right">Ca:Mg</th>
                    </tr>
                    </thead>
                    <tbody>
                    {BASE_SATURATION_BY_CEC_STATIC.map((row, i) => (
                        <tr key={i} className="border-t border-orange-200">
                            <td className="px-2 py-2">{row.cecRange}</td>
                            <td className="px-2 py-2 text-right">{row.operatingCec}</td>
                            <td className="px-2 py-2 text-right">{(row.ca * 100).toFixed(2)}</td>
                            <td className="px-2 py-2 text-right">{(row.mg * 100).toFixed(2)}</td>
                            <td className="px-2 py-2 text-right">{(row.k * 100).toFixed(2)}</td>
                            <td className="px-2 py-2 text-right">{row.caMgRatio.toFixed(4)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </StaticTable>

            <StaticTable title="Element Specific Gravity (mg/kg per meq/100g)">
                <div className="p-4 grid grid-cols-4 gap-4">
                    <div><span className="font-medium">Ca:</span> {ELEMENT_SPECIFIC_GRAVITY.Ca}</div>
                    <div><span className="font-medium">Mg:</span> {ELEMENT_SPECIFIC_GRAVITY.Mg}</div>
                    <div><span className="font-medium">K:</span> {ELEMENT_SPECIFIC_GRAVITY.K}</div>
                    <div><span className="font-medium">Na:</span> {ELEMENT_SPECIFIC_GRAVITY.Na}</div>
                </div>
            </StaticTable>
        </SectionBlock>
    )
}

const GypsumSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.gypsum

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({gypsum: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Gypsum Application">
            <SubSection title="Parameters">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <BooleanField label="Break Activated" value={params.breakActivated} onChange={v => update('breakActivated', v)} />
                    <NumberField label="Min Na %" value={params.minNaPercentage * 100} onChange={v => update('minNaPercentage', v / 100)} unit="%" fractionDigits={2} />
                    <NumberField label="Min Application" value={params.minApplicationKgHa} onChange={v => update('minApplicationKgHa', v)} unit="kg/ha" />
                    <NumberField label="Final Na Leaching Level" value={params.finalNaLeachingLevel} onChange={v => update('finalNaLeachingLevel', v)} fractionDigits={2} />
                    <FieldRow label="Purpose">
                        <SelectEnum
                            value={params.purpose}
                            onChange={v => update('purpose', v as GypsumPurpose)}
                            options={{
                                'Leaching Na': GypsumPurpose.LeachingNa,
                                'Supplying Ca': GypsumPurpose.CaSource,
                                'Leaching Mg': GypsumPurpose.LeachingMg
                            }}
                        />
                    </FieldRow>
                    <FieldRow label="Main Ca Source">
                        <SelectEnum
                            value={params.mainCaSource}
                            onChange={v => update('mainCaSource', v as MainCaSource)}
                            options={{
                                'Lime': MainCaSource.Lime,
                                'Gypsum': MainCaSource.Gypsum
                            }}
                        />
                    </FieldRow>
                    <BooleanField label="Leach Mg in Prediction" value={params.leachMgInPrediction} onChange={v => update('leachMgInPrediction', v)} />
                    <NumberField label="Depth" value={params.depthCm} onChange={v => update('depthCm', v)} unit="cm" />
                    <BooleanField label="Max Break Activated" value={params.maxBreakActivated} onChange={v => update('maxBreakActivated', v)} />
                </div>
            </SubSection>

            <SubSection title="Maximum Gypsum by CEC">
                <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="px-3 py-2 text-left">CEC</th>
                        <th className="px-3 py-2 text-right">Max Gypsum (kg/ha)</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.entries(params.rules).map(([key, rule]: [string, any]) => (
                        <tr key={key} className="border-t border-gray-200">
                            <td className="px-3 py-2">{rule.cec}</td>
                            <td className="px-3 py-2 text-right">
                                <Number value={rule.maxKg} change={v => {
                                    const updated = {...params.rules, [key]: {...rule, maxKg: v}}
                                    update('rules', updated)
                                }} />
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </SubSection>
        </SectionBlock>
    )
}

const PotassiumSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.potassium

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({potassium: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Potassium (K) Application">
            <SubSection title="Parameters">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <FieldRow label="Recommendation Basis">
                        <SelectEnum
                            value={params.recommendationBasis}
                            onChange={v => update('recommendationBasis', v as KRecommendationBasis)}
                            options={{
                                'K Base Saturation': KRecommendationBasis.KBaseSaturation,
                                'K mg/kg': KRecommendationBasis.IdealKMgKg
                            }}
                        />
                    </FieldRow>
                    <NumberField label="Ideal K" value={params.idealMgKg} onChange={v => update('idealMgKg', v)} unit="mg/kg" />
                    <BooleanField label="Break Activated" value={params.breakActivated} onChange={v => update('breakActivated', v)} />
                    <BooleanField label="Split Over 2 Years" value={params.splitOver2Years} onChange={v => update('splitOver2Years', v)} />
                </div>
            </SubSection>

            <SubSection title="K Break Points - Max Application (kg/ha)">
                <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="px-3 py-2 text-left">Break Point</th>
                        <th className="px-3 py-2 text-right">Max Application (kg/ha)</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(['k1', 'k2', 'k3', 'k4', 'k5', 'k6', 'k7'] as const).map((key) => (
                        <tr key={key} className="border-t border-gray-200">
                            <td className="px-3 py-2">{key.toUpperCase()}</td>
                            <td className="px-3 py-2 text-right">
                                <Number value={params[key].maxApplicationKgHa} change={v => {
                                    update(key, {...params[key], maxApplicationKgHa: v})
                                }} />
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </SubSection>
        </SectionBlock>
    )
}

const NutricastSection: React.FC<{store: TemplateStore}> = ({store}) => {
    const {data, set} = store
    const params = data.nutricast

    const update = <K extends keyof typeof params>(key: K, value: typeof params[K]) => {
        set({nutricast: {...params, [key]: value}})
    }

    return (
        <SectionBlock title="Nutricast">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <BooleanField label="Determination Enabled" value={params.determinationEnabled} onChange={v => update('determinationEnabled', v)} />
                <BooleanField label="In Prediction" value={params.inPrediction} onChange={v => update('inPrediction', v)} />
                <FieldRow label="Application Basis">
                    <Input
                        value={params.applicationBasis}
                        change={v => update('applicationBasis', v)}
                    />
                </FieldRow>
                <NumberField label="Surface Application %" value={params.surfaceApplicationPercent * 100} onChange={v => update('surfaceApplicationPercent', v / 100)} unit="%" fractionDigits={0} />
                <NumberField label="Planting Holes per ha" value={params.plantingHolesPerHa || 0} onChange={v => update('plantingHolesPerHa', v || null)} />
                <NumberField label="Planting Hole Size" value={params.plantingHoleSizeM2 || 0} onChange={v => update('plantingHoleSizeM2', v || null)} unit="m²" fractionDigits={2} />
                <NumberField label="Depth" value={params.depthCm} onChange={v => update('depthCm', v)} unit="cm" />
                <NumberField label="Max Application" value={params.maxApplicationKgHa} onChange={v => update('maxApplicationKgHa', v)} unit="kg/ha" />
                <NumberField label="Ideal K" value={params.idealKMgKg} onChange={v => update('idealKMgKg', v)} unit="mg/kg" />
                <NumberField label="Ideal P" value={params.idealPMgKg} onChange={v => update('idealPMgKg', v)} unit="mg/kg" />
            </div>
        </SectionBlock>
    )
}

interface UpsertLimingTemplateProps {
    footer?: React.ReactNode
    backUrl?: string
}

const UpsertLimingTemplate: React.FC<UpsertLimingTemplateProps> = (props) => {
    const context = useContext(AppContext)
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const [searchParams] = useSearchParams()
    const copyFromId = searchParams.get('copy')
    const isEditMode = id !== undefined
    const isApprovalMode = props.footer !== undefined

    const [templateData, setTemplateDataInternal] = useState<TemplateData | null>(null)
    const [name, setNameInternal] = useState('')
    const [description, setDescriptionInternal] = useState('')
    const templateId = useRef<number | null>(id ? parseInt(id) : null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)

    const [currentStep, setCurrentStep] = useState(0)
    const stepperRef = useRef<StepperRef | null>(null)

    // Approval state
    const [readyForApproval, setReadyForApproval] = useState(false)
    const [approved, setApproved] = useState(false)
    const [showApproverDialog, setShowApproverDialog] = useState(false)
    const [showResubmitWarning, setShowResubmitWarning] = useState(false)

    // Wrap setters to track changes
    const setName = (value: string) => {
        setNameInternal(value)
        setHasChanges(true)
    }
    const setDescription = (value: string) => {
        setDescriptionInternal(value)
        setHasChanges(true)
    }
    const setTemplateData = (value: TemplateData | null) => {
        setTemplateDataInternal(value)
        setHasChanges(true)
    }

    // Block navigation when there are unsaved changes
    const blocker = useBlocker(_ => hasChanges)

    const BackButton = () => (
        <button className="btn bg-primary m-2" onClick={() => navigate(props.backUrl || '/liming/templates/')}>
            {context.word('back')}
        </button>
    )

    // Clean up hasChanges on unmount to prevent blocker race conditions
    useEffect(() => {
        return () => {
            setHasChanges(false)
        }
    }, [])

    useEffect(() => {
        if (isEditMode) {
            // Edit mode: load existing template
            LimeController.get(parseInt(id)).then(data => {
                if (data) {
                    setNameInternal(data.name)
                    setDescriptionInternal(data.description)
                    setTemplateDataInternal(data.data)
                    setReadyForApproval(data.readyForApproval)
                    setApproved(data.approved)
                }
                setLoading(false)
            }).catch(err => {
                console.error('Failed to load template:', err)
                setLoading(false)
            })
        } else if (copyFromId) {
            // Copy mode: load template data but clear name/description
            LimeController.get(parseInt(copyFromId)).then(data => {
                if (data) {
                    setTemplateDataInternal(data.data)
                    // Name and description stay empty for new template
                }
                setLoading(false)
            }).catch(err => {
                console.error('Failed to load template to copy:', err)
                setLoading(false)
            })
        } else {
            // Create mode: load default template data structure
            LimeController.getPlayField().then(data => {
                setTemplateDataInternal(data.templateData)
                setLoading(false)
            }).catch(err => {
                console.error('Failed to load default template:', err)
                setLoading(false)
            })
        }
    }, [id, isEditMode, copyFromId])

    if (loading) {
        return (
            <div>
                <BackButton/>
                <div className="bg-white shadow rounded-lg">
                    <p className="text-gray-500">Loading...</p>
                </div>
            </div>
        )
    }

    if (!templateData) {
        return (
            <div>
                <BackButton/>
                <div className="bg-white shadow rounded-lg">
                    <p className="text-red-500">Failed to load template</p>
                </div>
            </div>
        )
    }

    // Build store using buildSetter pattern
    const store: TemplateStore = {
        data: templateData,
        set: buildSetter(templateData, setTemplateData as React.Dispatch<TemplateData>)
    }

    const canSave = name.trim().length > 0

    const steps: StepTab[] = [
        {
            key: 'elemental-sulphur',
            text: 'Sulphur',
            content: () => <ElementalSulphurSection store={store} />
        },
        {
            key: 'surface-application',
            text: 'Surface',
            content: () => <SurfaceApplicationSection store={store} />
        },
        {
            key: 'lime',
            text: 'Lime',
            content: () => <LimeApplicationSection store={store} />
        },
        {
            key: 'phosphorus',
            text: 'Phosphorus',
            content: () => <PhosphorusSection store={store} />
        },
        {
            key: 'base-saturation',
            text: 'Base Sat.',
            content: () => <BaseSaturationSection store={store} />
        },
        {
            key: 'gypsum',
            text: 'Gypsum',
            content: () => <GypsumSection store={store} />
        },
        {
            key: 'potassium',
            text: 'Potassium',
            content: () => <PotassiumSection store={store} />
        },
        {
            key: 'nutricast',
            text: 'Nutricast',
            content: () => <NutricastSection store={store} />
        }
    ]

    const isFirstStep = currentStep === 0
    const isLastStep = currentStep === steps.length - 1

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleSave = async (): Promise<void> => {
        if (!templateData || !canSave) return

        setSaving(true)
        try {
            if (templateId.current) {
                // Update existing template
                await showSuccessOrFailed(
                    context,
                    LimeController.update({
                        id: templateId.current,
                        name: name.trim(),
                        description: description.trim(),
                        data: templateData
                    }),
                    'successfully_saved',
                    'failed_to_save'
                )
                setHasChanges(false)
                // Reset approval status as backend resets it on non-approver edit
                setReadyForApproval(false)
                setApproved(false)
            } else {
                // Create new template
                const result = await showSuccessOrFailed(
                    context,
                    LimeController.create({
                        name: name.trim(),
                        description: description.trim(),
                        data: templateData
                    }),
                    'successfully_saved',
                    'failed_to_save'
                )
                // Set templateId so subsequent saves update instead of create
                templateId.current = result.id
                setHasChanges(false)
            }
        } finally {
            setSaving(false)
        }
    }

    const handleSubmitForApproval = async (approverId: number) => {
        if (!templateId.current) return

        setShowApproverDialog(false)
        try {
            await LimeController.submitTemplate({ id: templateId.current, approverId })
            context.showSnack(<Success title={context.word('submitted_for_approval')} />)
            setReadyForApproval(true)
        } catch (err) {
            console.error('Failed to submit for approval:', err)
        }
    }
    
    return <>
        <Dialog title={context.word('save_template')}
                show={blocker.state == 'blocked'}
                setShow={() => blocker.reset?.()}
                body={
                    <div className="p-4">
                        <div className="text-center mb-4">
                            <div>{context.word('unsaved_templates_lost')}.</div>
                            <div>{context.word('save_before_closing')}?</div>
                        </div>
                        <div className="flex w-full justify-between items-center">
                            <div className="uppercase text-error-400 pr-4 cursor-pointer underline"
                                 onClick={() => {
                                     setHasChanges(false)
                                     blocker.proceed?.()
                                 }}>
                                {context.word('discard_changes')}
                            </div>
                            <div>
                                <div className="btn bg-primary"
                                     onClick={() => handleSave().then(_ => {
                                         setHasChanges(false)
                                         blocker.proceed?.()
                                     })}>{context.word('save_template')}</div>
                            </div>
                        </div>
                    </div>
                }
        />

        <div>
            <div className="flex items-center justify-between mb-6">
                <BackButton/>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                        <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded"></div>
                        <span>Editable</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <div className="w-4 h-4 bg-orange-50 border border-orange-200 rounded"></div>
                        <span>Static</span>
                    </div>
                </div>
            </div>

            {/* Template Name and Description */}
            <div className="bg-white shadow rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        {EditRow(
                            context.word('name'),
                            <Input
                                value={name}
                                change={setName}
                                placeHolder={context.word('enter_template_name')}
                                />
                        )}
                    </div>
                    <div>
                        {EditRow(
                            context.word('description'),
                            <Input
                                value={description}
                                change={setDescription}
                                placeHolder={context.word('enter_description')}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                <Stepper
                    ref={stepperRef}
                    tabs={steps}
                    tabMode="toggle"
                    activeIndex={currentStep}
                    onChange={(fromIndex, toIndex) => {
                        setCurrentStep(toIndex)
                        return true
                    }}
                />

                <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
                    {isFirstStep ? (
                        <div className="btn bg-gray-300 cursor-not-allowed">
                            {context.word('previous')}
                        </div>
                    ) : (
                        <div className="btn bg-gray-500" onClick={handlePrevious}>
                            {context.word('previous')}
                        </div>
                    )}

                    {isLastStep ? (
                        <div className="btn bg-gray-300 cursor-not-allowed">
                            {context.word('next')}
                        </div>
                    ) : (
                        <div className="btn bg-primary" onClick={handleNext}>
                            {context.word('next')}
                        </div>
                    )}
                </div>
            </div>

            {/* Save and Approval buttons - always visible at bottom */}
            <div className="flex justify-end gap-2 mt-6">
                {/* Save button - only show when there are changes */}
                {hasChanges && canSave && (
                    <div className="btn bg-primary" onClick={() => {
                        if (!isApprovalMode && (readyForApproval || approved)) {
                            setShowResubmitWarning(true)
                        } else {
                            handleSave()
                        }
                    }}>
                        {saving ? context.word('saving') : context.word('save_template')}
                    </div>
                )}

                {/* In approval mode, show the provided footer (approve/decline buttons) */}
                {isApprovalMode ? (
                    props.footer
                ) : (
                    /* Normal mode: show approval status/submit button */
                    templateId.current && (
                        approved ? (
                            <div className="btn bg-green-500">
                                {context.word('approved')}
                            </div>
                        ) : readyForApproval ? (
                            <div className="btn bg-gray-500">
                                {context.word('waiting_approval')}
                            </div>
                        ) : (
                            <div className="btn bg-primary" onClick={() => setShowApproverDialog(true)}>
                                {context.word('submit_approval')}
                            </div>
                        )
                    )
                )}
            </div>
        </div>

        {/* Approver selection dialog */}
        <Dialog
            title={context.word('select_approver')}
            show={showApproverDialog}
            setShow={setShowApproverDialog}
            body={
                <ProgramApprove
                    onSubmit={handleSubmitForApproval}
                    onCancel={() => setShowApproverDialog(false)}
                />
            }
        />

        {/* Resubmit warning dialog */}
        <Dialog
            title={context.word('warning')}
            show={showResubmitWarning}
            setShow={setShowResubmitWarning}
            body={
                <div className="p-2 text-xl">
                    <div>{context.word('must_resubmit_template_for_approval')}. {context.word('would_you_like_to_continue')}?</div>
                    <div className="flex m-2 text-white justify-center">
                        <button className="bg-primary p-2 rounded m-1 px-4" onClick={() => {
                            handleSave()
                            setShowResubmitWarning(false)
                        }}>
                            {context.word('yes')}
                        </button>
                        <button className="bg-error-500 p-2 rounded m-1 px-4" onClick={() => setShowResubmitWarning(false)}>
                            {context.word('no')}
                        </button>
                    </div>
                </div>
            }
        />
    </>
}

export default UpsertLimingTemplate
